import i18next from "i18next"
import { getSiteIdFromHost } from "@/sites/config"
import cs from "./cs.json"
import en from "./en.json"
import vi from "./vi.json"

type Locale = "vi" | "en" | "cs"
type TranslationDictionary = Record<string, string>

const SUPPORTED_LOCALES: Locale[] = ["vi", "en", "cs"]
const NON_DEFAULT_LOCALES: Locale[] = ["en", "cs"]
const I18N_NAMESPACE = "translation"
const I18N_API_PATH = "/api/dictionaries"
const I18N_CACHE_PREFIX = "i18n-translations"
const I18N_CACHE_TTL_MS = 5 * 60 * 1000
const DOMAIN_COOKIE = "domain-storage"
const NON_LOCALIZED_PREFIXES = new Set(["admin", "login", "api", "_next"])

const fallbackDictionaries: Record<Locale, TranslationDictionary> = {
  vi,
  en,
  cs,
}

type CachedTranslationsPayload = {
  website: string
  locale: Locale
  data: TranslationDictionary
  timestamp: number
}

type LoadTranslationsOptions = {
  force?: boolean
}

const pendingLoads = new Map<string, Promise<TranslationDictionary>>()
let explicitRuntimeLocale: Locale | null = null

function isSupportedLocale(value: string | undefined | null): value is Locale {
  return SUPPORTED_LOCALES.includes((value || "").trim() as Locale)
}

function normalizeLocale(value: string | undefined | null): Locale {
  return isSupportedLocale(value) ? value : "vi"
}

function getLocaleFromPathname(pathname: string): Locale | null {
  const parts = pathname.split("/").filter(Boolean)

  if (parts.length === 0) {
    return "vi"
  }

  if (NON_LOCALIZED_PREFIXES.has(parts[0])) {
    return null
  }

  if (parts[0] === "sites" && isSupportedLocale(parts[2])) {
    return parts[2]
  }

  if (NON_DEFAULT_LOCALES.includes(parts[0] as Locale)) {
    return parts[0] as Locale
  }

  return "vi"
}

function normalizeDictionary(input: unknown): TranslationDictionary {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return {}
  }

  return Object.entries(input).reduce<TranslationDictionary>((accumulator, [rawKey, rawValue]) => {
    const key = rawKey.trim()

    if (!key) {
      return accumulator
    }

    if (typeof rawValue === "string") {
      accumulator[key] = rawValue
    } else if (rawValue == null) {
      accumulator[key] = ""
    } else {
      accumulator[key] = String(rawValue)
    }

    return accumulator
  }, {})
}

function getCookieValue(name: string): string {
  if (typeof document === "undefined") {
    return ""
  }

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : ""
}

function parseSelectedWebsiteFromCookie(): string {
  const rawCookie = getCookieValue(DOMAIN_COOKIE)
  if (!rawCookie) {
    return ""
  }

  try {
    const parsed = JSON.parse(rawCookie) as {
      state?: {
        selectedDomain?: {
          id?: number
          attributes?: {
            domain?: string
          }
        }
      }
    }

    const selectedDomain = parsed?.state?.selectedDomain
    if (!selectedDomain || selectedDomain.id === 0) {
      return ""
    }

    return selectedDomain.attributes?.domain?.trim() || ""
  } catch {
    return ""
  }
}

function resolveCurrentWebsite(): string {
  if (typeof window === "undefined") {
    return ""
  }

  const selectedDomain = parseSelectedWebsiteFromCookie()
  if (selectedDomain) {
    return selectedDomain
  }

  return getSiteIdFromHost(window.location.host)
}

function getCurrentRuntimeLocale(): Locale {
  if (explicitRuntimeLocale) {
    return explicitRuntimeLocale
  }

  if (typeof window === "undefined") {
    return "vi"
  }

  const pathnameLocale = getLocaleFromPathname(window.location.pathname)
  if (pathnameLocale) {
    return pathnameLocale
  }

  const savedLanguage = window.localStorage.getItem("language")
  if (isSupportedLocale(savedLanguage)) {
    return savedLanguage
  }

  return normalizeLocale(document?.documentElement?.lang || "en")
}

function buildCacheKey(locale: Locale, website: string): string {
  return `${I18N_CACHE_PREFIX}:${website || "global"}:${locale}`
}

function matchesCacheKey(key: string, website?: string): boolean {
  if (!key.startsWith(`${I18N_CACHE_PREFIX}:`)) {
    return false
  }

  if (!website) {
    return true
  }

  return key.startsWith(`${I18N_CACHE_PREFIX}:${website}:`)
}

function readCachedTranslations(locale: Locale, website: string): TranslationDictionary | null {
  if (typeof window === "undefined") {
    return null
  }

  try {
    const raw = window.localStorage.getItem(buildCacheKey(locale, website))
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as CachedTranslationsPayload
    if (!parsed || typeof parsed !== "object") {
      return null
    }

    if (Date.now() - Number(parsed.timestamp || 0) > I18N_CACHE_TTL_MS) {
      return null
    }

    return normalizeDictionary(parsed.data)
  } catch {
    return null
  }
}

function writeCachedTranslations(locale: Locale, website: string, data: TranslationDictionary) {
  if (typeof window === "undefined") {
    return
  }

  const payload: CachedTranslationsPayload = {
    locale,
    website,
    data,
    timestamp: Date.now(),
  }

  try {
    window.localStorage.setItem(buildCacheKey(locale, website), JSON.stringify(payload))
  } catch {
    // Ignore storage quota issues.
  }
}

export function clearCachedTranslations(website?: string) {
  if (typeof window === "undefined") {
    return
  }

  try {
    const keysToRemove: string[] = []

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (!key || !matchesCacheKey(key, website)) {
        continue
      }

      keysToRemove.push(key)
    }

    keysToRemove.forEach(key => {
      window.localStorage.removeItem(key)
    })
  } catch {
    // Ignore storage errors.
  }
}

function applyTranslations(locale: Locale, data: TranslationDictionary) {
  const normalizedData = normalizeDictionary(data)
  i18next.addResourceBundle(locale, I18N_NAMESPACE, normalizedData, true, true)
}

export function hydrateI18nResources(localeInput: string, data: TranslationDictionary) {
  const locale = normalizeLocale(localeInput)
  applyTranslations(locale, data)
  syncI18nLanguage(locale)
}

function getInitialLanguage(): Locale {
  return getCurrentRuntimeLocale()
}

function getInitialResources() {
  const initialLanguage = getInitialLanguage()
  const initialWebsite = resolveCurrentWebsite()
  const cachedTranslations = readCachedTranslations(initialLanguage, initialWebsite)

  const resources = SUPPORTED_LOCALES.reduce<Record<Locale, { translation: TranslationDictionary }>>((accumulator, locale) => {
    accumulator[locale] = {
      translation: { ...fallbackDictionaries[locale] },
    }
    return accumulator
  }, {} as Record<Locale, { translation: TranslationDictionary }>)

  if (cachedTranslations) {
    resources[initialLanguage].translation = {
      ...resources[initialLanguage].translation,
      ...cachedTranslations,
    }
  }

  return resources
}

async function fetchTranslationsFromApi(locale: Locale, website: string): Promise<TranslationDictionary> {
  const params = new URLSearchParams({ locale })
  if (website) {
    params.set("website", website)
  }

  const response = await fetch(`${I18N_API_PATH}?${params.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    cache: "no-store",
  })

  if (!response.ok) {
    throw new Error(`Failed to load translations: ${response.status} ${response.statusText}`)
  }

  return normalizeDictionary(await response.json())
}

export async function loadTranslations(
  localeInput?: string,
  websiteInput?: string,
  options: LoadTranslationsOptions = {},
): Promise<TranslationDictionary> {
  const locale = normalizeLocale(localeInput)
  const website = (websiteInput || resolveCurrentWebsite()).trim()
  const cacheKey = buildCacheKey(locale, website)

  if (!options.force) {
    const cachedTranslations = readCachedTranslations(locale, website)
    if (cachedTranslations) {
      applyTranslations(locale, cachedTranslations)
    }
  }

  if (!options.force && pendingLoads.has(cacheKey)) {
    return pendingLoads.get(cacheKey)!
  }

  const request = fetchTranslationsFromApi(locale, website)
    .then(translations => {
      applyTranslations(locale, translations)
      writeCachedTranslations(locale, website, translations)
      return translations
    })
    .catch(error => {
      if (!i18next.hasResourceBundle(locale, I18N_NAMESPACE)) {
        applyTranslations(locale, fallbackDictionaries[locale])
      }
      throw error
    })
    .finally(() => {
      pendingLoads.delete(cacheKey)
    })

  pendingLoads.set(cacheKey, request)
  return request
}

export async function changeI18nLanguage(localeInput: string, websiteInput?: string) {
  const locale = syncI18nLanguage(localeInput)

  try {
    await loadTranslations(locale, websiteInput)
  } catch (error) {
    console.warn("Failed to refresh translations from API, using fallback resources.", error)
  }

  await i18next.changeLanguage(locale)
}

export function syncI18nLanguage(localeInput?: string): Locale {
  const locale = localeInput ? normalizeLocale(localeInput) : getCurrentRuntimeLocale()

  if (localeInput) {
    explicitRuntimeLocale = locale
  }

  if (i18next.isInitialized && i18next.language !== locale) {
    void i18next.changeLanguage(locale)
  }

  return locale
}

if (!i18next.isInitialized) {
  i18next.init({
    interpolation: { escapeValue: false },
    lng: getInitialLanguage(),
    fallbackLng: "en",
    resources: getInitialResources(),
  })
}

const t = (key: string, options?: any) => {
  syncI18nLanguage()
  return i18next.t(key, options).toString()
}

/**
 * Locale-scoped translator. Unlike `t`, it does NOT read the global runtime
 * locale — it always resolves against the given `locale`. Use this inside
 * UI-builder blocks so static labels follow the block's locale (from
 * `useUIBuilderLocaleStore` / route param) instead of the editor's language.
 */
const getFixedT = (localeInput?: string) => {
  const locale = normalizeLocale(localeInput)
  return (key: string, options?: any) => {
    // Support i18next's `t(key, defaultValue)` string signature alongside the options object.
    if (typeof options === "string") {
      return i18next.t(key, { lng: locale, defaultValue: options }).toString()
    }
    return i18next.t(key, { lng: locale, ...options }).toString()
  }
}

export { i18next, t, getFixedT }
